import { Icon, Input, notification, Select, Text, theme, Tooltip } from "@findemdev/maui";
import { SetStateAction, useCallback, useContext, useMemo } from "react";
// styled components + components
import { MyCredentialTitle, TabItem, TabsContainer, TitleSectionWrapper } from "./Credentials.styled";
// utils
import { CREDENTIALS_VIEW_TYPE } from "../../util/constants";
// types
import { CredentialsViewMode } from "../../types/common";
import { ICredentialsResponse } from "../../types/credential";
import { CredNameFilterObj, FilterType } from "./Filters/constants";
import { IFilterObj } from "./Filters/types";
import { FAVORITES_TAB, NAVIGATION_TABS, TabId } from "./NavigationTabs";
import UserContext from "../../../../contexts/UserContext";
import { useFetchAllUsersQuery } from "../../../../services/payments";
import { decodeString } from "@findemdev/findem-helpers";
import { useAppSelector } from "../../hooks/storeHooks";
import { selectSelectedUserId } from "../../reducers/credentialAppSlice";

interface ITitleSectionProps {
  activeViewMode: CredentialsViewMode;
  activeTab: TabId;
  credentialsData: ICredentialsResponse | undefined;
  filterObj: IFilterObj;
  setFilterObj: (value: SetStateAction<IFilterObj>) => void;
  handleTabChange: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  onUserChange: (userId: string | undefined) => void;
}

const TitleSection = ({
  activeViewMode,
  activeTab,
  credentialsData,
  filterObj,
  setFilterObj,
  handleTabChange,
  onUserChange,
}: ITitleSectionProps) => {
  const userModel = useContext(UserContext);
  const selectedUserId = useAppSelector(selectSelectedUserId);
  const { data: allUsers, isFetching: isFetchingAllUsers } = useFetchAllUsersQuery(
    { joid: userModel.getUser().uid },
    { skip: !userModel.isCredAdmin() },
  );
  const title = [...NAVIGATION_TABS, FAVORITES_TAB].find((tab) => tab.id === activeTab)?.label || "My Credentials";
  const userOptions = useMemo(() => allUsers?.users.map((user) => {
    const name = decodeString(user.local.name);

    return {
      value: user._id,
      label: <Tooltip
        overlayInnerStyle={{ whiteSpace: "nowrap" }}
        overlayStyle={{ maxWidth: "none" }}
        title={decodeString(user.local.email)}
      >
        <span style={{ display: "block", width: "100%" }}>{name}</span>
      </Tooltip>,
      name,
    };
  }) ?? [], [allUsers]);

  // callback
  const handleCredSearch = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (CredNameFilterObj.autoCompleteKeyword) {
        setFilterObj({
          ...filterObj,
          [FilterType.NAME]: {
            field: CredNameFilterObj.autoCompleteKeyword,
            operator: CredNameFilterObj.operator,
            value: event.target.value,
          },
        });
      }

    } catch(error) {
      console.error("failed to search cred: ", error);
      notification.error({
        message: "Failed to search credentials",
      });
    }
  }, []);

  return (
    <TitleSectionWrapper>
      <MyCredentialTitle>
        <Icon icon="grid_view" $color={theme.colors['grey-500']} $size={20} />
        <Text variant="defaultHeader">{title}</Text>
        <Text
          variant="mediumLead"
          options={[
            'muted'
          ]}
        >
          {credentialsData?.count ?? 0}
        </Text>
      </MyCredentialTitle>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",
      }}>
        {userModel.isCredAdmin() && (
          <Select
            allowClear
            aria-label="Switch user"
            className={selectedUserId && selectedUserId !== userModel.getUser().uid
              ? "credential-user-select--impersonating"
              : undefined}
            filterOption={(input, option) =>
              (option?.name ?? "").toLowerCase().includes(input.toLowerCase())
            }
            loading={isFetchingAllUsers}
            onChange={(value) => onUserChange(value ? String(value) : undefined)}
            optionLabelProp="name"
            options={userOptions}
            placeholder="Switch user"
            showSearch
            style={{
              width: "210px",
              marginRight: theme.dimensions.spacing[1],
            }}
            value={selectedUserId}
          />
        )}
        <Input
          allowClear={true}
          onChange={handleCredSearch}
          debounceDelay={500}
          prefix={<Icon icon="search" $size={20} $color={theme.colors['grey-600']} />}
          placeholder={"Search..."}
          style={{
            width: "210px",
            marginRight: theme.dimensions.spacing[1],
          }}
        />
        <TabsContainer>
          {CREDENTIALS_VIEW_TYPE.map((option) =>
            <TabItem
              $selected={activeViewMode === option.value}
              onClick={handleTabChange}
              data-tab={option.value}
              key={option.value}
            >
              <Icon icon={option.icon} $size={16} $color={theme.colors['grey-500']} />
            </TabItem>
          )}
          </TabsContainer>
      </div>
    </TitleSectionWrapper>
  );
};

export default TitleSection;
